
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { PlatformFeeConfig } from '../types';
import { useTranslation } from '../translations';

interface LandingPageProps {
  onGetStarted: () => void;
  config: PlatformFeeConfig;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, config }) => {
  const isVideo = config.landingBackgroundType === 'video';
  const backgroundUrl = isVideo ? config.landingVideoUrl : config.landingImageUrl;
  const lang = (localStorage.getItem('sl_lang') || 'en') as any;
  const t = useTranslation(lang);

  // Stable cache key to prevent flickering on re-renders, but allow force reload
  const [imageError, setImageError] = React.useState(false);
  const [mediaKey, setMediaKey] = React.useState(Date.now());
  const cacheBustedUrl = backgroundUrl ? `${backgroundUrl}${backgroundUrl.includes('?') ? '&' : '?'}t=${mediaKey}` : '';

  // Fallback image in case of error
  const fallbackImage = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&h=1080&fit=crop';

  const handleImageError = () => {
    console.error('❌ Failed to load background image:', backgroundUrl);

    // Detect common mistake: using the web page URL instead of the image source
    if (backgroundUrl?.includes('unsplash.com/photos/')) {
      console.warn('⚠️ DETECTED WEB PAGE URL: It looks like you are using an Unsplash photo page URL instead of the image source URL. Please use the direct image address (e.g. right-click -> copy image address).');
    }

    setImageError(true);
  };

  // Force reload when background URL or type changes
  React.useEffect(() => {
    console.log('🎨 Landing Page Background Changed:', {
      type: isVideo ? 'video' : 'image',
      url: backgroundUrl,
      mediaKey: mediaKey + 1
    });
    setMediaKey(Date.now());
    setImageError(false);
  }, [backgroundUrl, isVideo]);

  return (
    <div className="absolute inset-0 z-[80] flex flex-col items-center justify-end bg-black overflow-hidden animate-fadeIn">
      {/* Cinematic Dynamic Background */}
      <div className="absolute inset-0 z-0">
        {isVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            key={`${backgroundUrl}-${mediaKey}`}
            className="w-full h-full object-cover object-center opacity-70 animate-fadeIn"
            onError={() => console.error('❌ Failed to load background video:', backgroundUrl)}
          >
            <source src={backgroundUrl} type="video/mp4" />
          </video>
        ) : (
          <img
            src={imageError ? fallbackImage : (cacheBustedUrl || fallbackImage)}
            onError={handleImageError}
            key={`${backgroundUrl}-${mediaKey}`}
            className="w-full h-full object-cover object-center opacity-70 animate-fadeIn"
            alt="Hum Hena Background"
          />
        )}

        {/* Soft Vignette Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
      </div>

      {/* Action Section - Only "Get Started Now" */}
      <div className="relative z-10 w-full px-4 sm:px-6 md:px-8 pb-12 sm:pb-16 md:pb-20 safe-bottom animate-slideUp">
        <button
          onClick={onGetStarted}
          className="group relative w-full max-w-md mx-auto h-16 sm:h-18 md:h-20 bg-blue-600 rounded-2xl sm:rounded-[2.5rem] flex items-center justify-center gap-3 sm:gap-4 transition-all active:scale-95 shadow-[0_20px_50px_rgba(255,106,13,0.4)] hover:bg-blue-700 overflow-hidden border border-white/20"
        >
          {/* Button Text */}
          <span className="text-white text-lg sm:text-xl font-black tracking-tight z-10">{t.get_started}</span>

          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center group-hover:translate-x-1 transition-transform z-10 shadow-lg">
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          </div>

          {/* Subtle Pulse Layer */}
          <div className="absolute inset-0 rounded-2xl sm:rounded-[2.5rem] ring-4 ring-white/30 animate-pulse"></div>

          {/* Glossy highlight effect */}
          <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:left-[100%] transition-all duration-1000"></div>
        </button>

        {/* Footer Technical ID */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-white/30 text-[7px] sm:text-[8px] font-black uppercase tracking-[0.4em] sm:tracking-[0.5em]">
            Hyperlocal Protocol Secured • v1.2 (Live)
          </p>
        </div>
      </div>
    </div>
  );
};
